import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from 'lucide-react';

interface ProductGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  productName: string;
  productModel: string;
  brand: string;
}

export const ProductGalleryModal: React.FC<ProductGalleryModalProps> = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  productName,
  productModel,
  brand,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Sincronizar índice inicial cuando se abre el modal
  const [prevInitialIndex, setPrevInitialIndex] = useState(initialIndex);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen || initialIndex !== prevInitialIndex) {
    setPrevIsOpen(isOpen);
    setPrevInitialIndex(initialIndex);
    setCurrentIndex(initialIndex);
    setZoomLevel(1);
  }

  const handleNext = useCallback(() => {
    setZoomLevel(1);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setZoomLevel(1);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Atajos de Teclado (Escape, Flechas) y Bloqueo de Scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose, handleNext, handlePrev]);

  if (!isOpen || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-between bg-black/95 backdrop-blur-2xl text-white select-none animate-in fade-in-0 duration-200"
      onClick={onClose}
    >
      {/* 1. Header del Visor / Lightbox */}
      <div
        className="w-full px-4 sm:px-8 py-4 flex items-center justify-between border-b border-neutral-800/80 bg-neutral-950/80 z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="px-2 py-0.5 rounded bg-brand-primary text-white text-[11px] font-black uppercase tracking-wider">
            {brand}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-white truncate max-w-md">
              {productName}
            </h3>
            {productModel && <p className="text-xs text-neutral-400 font-mono">Modelo: {productModel}</p>}
          </div>
        </div>

        {/* Controles Centrales & Cierre */}
        <div className="flex items-center gap-3">
          {/* Contador de Fotos */}
          <div className="px-3 py-1 rounded-full bg-neutral-800 text-xs font-semibold text-neutral-300 font-mono">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Controles de Zoom */}
          <div className="hidden sm:flex items-center gap-1 bg-neutral-800/80 p-1 rounded-xl border border-neutral-700">
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 1}
              className="p-1.5 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-700 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
              title="Alejar (-)"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-[11px] font-mono px-1.5 text-neutral-300">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 3}
              className="p-1.5 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-700 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
              title="Acercar (+)"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            {zoomLevel > 1 && (
              <button
                type="button"
                onClick={handleResetZoom}
                className="p-1.5 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-700 cursor-pointer"
                title="Restablecer tamaño original"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Botón de Cierre */}
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-neutral-800/90 hover:bg-neutral-700 text-neutral-300 hover:text-white transition-colors cursor-pointer"
            aria-label="Cerrar visor de fotos (Escape)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* 2. Área Central de Visualización con Flechas */}
      <div
        className="flex-1 relative flex items-center justify-center p-4 sm:p-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Flecha Izquierda */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-4 sm:left-8 z-30 p-3 sm:p-4 rounded-full bg-neutral-900/80 hover:bg-brand-primary text-white backdrop-blur-md shadow-xl border border-neutral-700/80 hover:border-brand-primary transition-all hover:scale-110 cursor-pointer"
            aria-label="Foto anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Imagen Activa con Transformación de Zoom */}
        <div
          className="max-w-5xl max-h-[70vh] flex items-center justify-center overflow-auto transition-transform duration-200"
          style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
        >
          <img
            src={images[currentIndex]}
            alt={`${productName} - Vista ${currentIndex + 1}`}
            className="max-h-[68vh] max-w-full object-contain rounded-xl shadow-2xl transition-transform duration-200"
            style={{ transform: `scale(${zoomLevel})` }}
          />
        </div>

        {/* Flecha Derecha */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-4 sm:right-8 z-30 p-3 sm:p-4 rounded-full bg-neutral-900/80 hover:bg-brand-primary text-white backdrop-blur-md shadow-xl border border-neutral-700/80 hover:border-brand-primary transition-all hover:scale-110 cursor-pointer"
            aria-label="Siguiente foto"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* 3. Footer: Tira de Miniaturas Interactiva */}
      {images.length > 1 && (
        <div
          className="w-full px-4 py-3 bg-neutral-950/90 border-t border-neutral-800/80 flex items-center justify-center gap-3 overflow-x-auto z-20"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => {
            const isActive = idx === currentIndex;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setZoomLevel(1);
                  setCurrentIndex(idx);
                }}
                className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 cursor-pointer bg-neutral-900 ${
                  isActive
                    ? 'border-brand-primary ring-2 ring-brand-primary/40 scale-105 shadow-lg'
                    : 'border-neutral-700/60 opacity-60 hover:opacity-100 hover:border-neutral-500'
                }`}
                aria-label={`Ver miniatura ${idx + 1}`}
              >
                <img
                  src={img}
                  alt={`Miniatura ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
